<script setup lang="ts">
import {
  canBeTemplateEditor,
  type TemplateStatus,
  type UserSummary,
} from '@beeboard/common';
import { required } from '@vuelidate/validators';
import {
  useTemplateQualitySummary,
  type TemplateQualitySummary,
} from '@/composables/useTemplateQualitySummary';
import { useProfileStore } from '@/store/profileStore';
import { useTemplateRulesStore } from '@/store/templateRulesStore';
import { useTemplateStore } from '@/store/templateStore';
import { useUserRulesStore } from '@/store/userRulesStore';
import { useUserStore } from '@/store/userStore';
import { MultiSelectOption } from '@/ui/form/UiMultiSelect.vue';
import TemplateCustomProperties from './TemplateCustomProperties.vue';
import TemplateStatusSwitcher from './TemplateStatusSwitcher.vue';
import UserTile from './UserTile.vue';

interface Props {
  templateId?: string;
  manageOwner?: boolean;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();
const templateStore = useTemplateStore();
const profileStore = useProfileStore();
const userStore = useUserStore();
const message = useAlerts();
const { loadQualitySummary } = useTemplateQualitySummary();
const router = useRouter();
const route = useRoute();

const formData = ref<{
  title: string;
  scopes: string[];
}>({
  title: '',
  scopes: [],
});

const customProperties = ref<Record<string, unknown>>({});
const hasCustomPropertyErrors = ref(false);
const hasCustomPropertyChanges = ref(false);

const rules = {
  title: [required],
  scopes: [required],
};

const ownerId = ref('');
const keepPreviousOwnerAsEditor = ref(false);
const previousOwnerReaderScopes = ref<string[]>([]);
// Template reader scopes of the picked owner. Besides its own scopes a template may
// only get scopes its owner after the save holds as reader (checkUpdateTemplateDetails),
// so the options follow the pick. A new template gets the creator scopes of the user
// creating it (checkCreateTemplate).
const ownerScopes = ref<string[]>([]);
const readerScopesByUser = new Map<string, string[]>();
const initialScopes = ref<string[]>([]);
const areScopesLoaded = ref(false);
const isOwnerSelectOpen = ref(false);

const isLoading = ref(false);
const isStatusLoading = ref(false);
const scopeOptions = computed((): MultiSelectOption[] => {
  if (!areScopesLoaded.value) {
    return [];
  }

  return Array.from(new Set([...initialScopes.value, ...ownerScopes.value]))
    .filter(userStore.hasScope)
    .map((value) => {
      const scope = userStore.scope(value);

      return {
        label: scope.name,
        value: scope.id,
      };
    });
});

const templateLink = computed(() =>
  props.templateId
    ? templateStore.templateBasics(props.templateId, props.manageOwner)
    : undefined,
);

// Filled in setup, not onMounted: TemplateCustomProperties takes the first values it
// gets as unchanged, and values filled in later would count as the user's changes.
if (templateLink.value) {
  const template = templateLink.value;
  ownerId.value = template.owner?.id ?? '';
  formData.value.title = template.title;
  formData.value.scopes = [...template.scopes];
  if (template.custom_properties) {
    customProperties.value = { ...template.custom_properties };
  }
}
const initialTitle = formData.value.title;
initialScopes.value = [...formData.value.scopes];

const status = computed(() => templateLink.value?.status);

const scopesKey = computed(() => [...formData.value.scopes].sort().join(','));
const eligibleOwners = ref<UserSummary[]>([]);
// The `scopesKey` that `eligibleOwners` was loaded for.
const eligibleOwnersScopesKey = ref<string>();
const pickedOwner = ref<UserSummary>();

// The current owner, and a picked owner the scopes no longer cover, stay listed so
// that the select can still show them.
const ownerCandidates = computed(() => {
  const candidates = [...eligibleOwners.value];
  for (const user of [pickedOwner.value, templateLink.value?.owner]) {
    if (user && !candidates.some((candidate) => candidate.id === user.id)) {
      candidates.unshift(user);
    }
  }

  return candidates;
});

const ownerOptions = computed(() =>
  ownerCandidates.value.map(({ id, first_name, last_name, email }) => ({
    value: id,
    label: `${first_name} ${last_name} ${email}`,
  })),
);

const isOwnerChanged = computed(
  () =>
    canManageTemplate.value &&
    !!ownerId.value &&
    ownerId.value !== templateLink.value?.owner?.id,
);

// Shown instead of dropping the pick, which saved the template without the handover.
const ownerError = computed(() =>
  isOwnerChanged.value &&
  eligibleOwnersScopesKey.value === scopesKey.value &&
  !eligibleOwners.value.some((user) => user.id === ownerId.value)
    ? $t('template.ownerNeedsTemplateScopes')
    : undefined,
);

const canKeepPreviousOwnerAsEditor = computed(() =>
  canBeTemplateEditor(formData.value.scopes, previousOwnerReaderScopes.value),
);

const ownerChangeWarning = computed(() => {
  if (!isMeOwner.value) {
    return $t('company.updateTemplateOwnerWarning');
  }

  return keepPreviousOwnerAsEditor.value && canKeepPreviousOwnerAsEditor.value
    ? $t('templateEditor.confirmOwnerAlertKeepEditor')
    : $t('templateEditor.confirmOwnerAlert');
});

const previousOwnerName = computed(() => {
  const owner = templateLink.value?.owner;
  const name = [owner?.first_name, owner?.last_name].filter(Boolean).join(' ');

  return name || owner?.email || '';
});

let ownerCandidatesRequest = 0;

async function loadOwnerCandidates() {
  const key = scopesKey.value;
  const request = ++ownerCandidatesRequest;
  // Counted anyway: a request for other scopes may still be on its way.
  if (key === eligibleOwnersScopesKey.value) {
    return;
  }
  try {
    const users = await userStore.fetchEligibleOwners(formData.value.scopes);
    if (request === ownerCandidatesRequest) {
      eligibleOwners.value = users;
      eligibleOwnersScopesKey.value = key;
    }
  } catch (error) {
    if (request === ownerCandidatesRequest) {
      message.error(error);
    }
  }
}

watch(
  () => [...formData.value.scopes],
  () => {
    if (canManageTemplate.value) {
      void loadOwnerCandidates();
    }
  },
);

async function readerScopesOf(userId: string) {
  if (userId === profileStore.profile.id) {
    return profileStore.profile.scopes.template_reader ?? [];
  }
  if (!readerScopesByUser.has(userId)) {
    const user = await userStore.loadUserProfile(userId);
    readerScopesByUser.set(userId, user.scopes?.template_reader ?? []);
  }

  return readerScopesByUser.get(userId) ?? [];
}

watch(ownerId, (userId) => {
  pickedOwner.value = eligibleOwners.value.find((user) => user.id === userId);
  if (!userId) {
    ownerScopes.value = [];

    return;
  }
  void readerScopesOf(userId)
    .then((scopes) => {
      if (ownerId.value === userId) {
        ownerScopes.value = scopes;
      }
    })
    .catch(message.error);
});

function getOwner(userId: string | number | undefined) {
  const user = ownerCandidates.value.find((u) => u.id === userId);

  return {
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    email: user?.email || '',
  };
}

const isMeOwner = computed(
  () => templateLink.value?.owner?.id === profileStore.profile.id,
);

// Lifecycle lives on the existing template, so it is only offered when editing,
// and only to the owner -- or to a template admin in Company → Templates.
const canManageTemplate = computed(
  () =>
    !!templateLink.value &&
    (isMeOwner.value ||
      (props.manageOwner && useUserRulesStore().canUserManageTemplateOwner())),
);

const subtitle = computed(() =>
  props.templateId
    ? $t('template.editProjectMapTemplate')
    : $t('template.createNewProjectMap'),
);

/**
 * Applies a status. The confirmations live in `selectStatus` below, so this
 * stays the plain "write it" step.
 */
async function setStatus(next: TemplateStatus) {
  if (!props.templateId || next === status.value) {
    return;
  }

  isStatusLoading.value = true;

  return templateStore
    .updateStatus(props.templateId, next)
    .then(() => message.success($t('template.statusChanged')))
    .catch((err) => message.error(err ?? $t('template.statusChangeFailed')))
    .finally(() => {
      isStatusLoading.value = false;
    });
}

const inactiveConfirmIsVisible = ref(false);

const releaseConfirmIsVisible = ref(false);
const isQualityLoading = ref(false);
const qualitySummary = ref<TemplateQualitySummary | null>(null);

const qualityFindingCount = computed(() =>
  qualitySummary.value
    ? qualitySummary.value.warnings + qualitySummary.value.hints
    : 0,
);

interface QualityAlert {
  variant: 'info' | 'success' | 'warning';
  key: string;
  params: Record<string, number>;
}

const qualityAlert = computed((): QualityAlert => {
  if (isQualityLoading.value) {
    return {
      variant: 'info',
      key: 'template.releaseQualityLoading',
      params: {},
    };
  }
  if (!qualitySummary.value) {
    return {
      variant: 'info',
      key: 'template.releaseQualityUnavailable',
      params: {},
    };
  }
  if (qualityFindingCount.value) {
    return {
      variant: 'warning',
      key: 'template.releaseQualitySummary',
      params: {
        warnings: qualitySummary.value.warnings,
        hints: qualitySummary.value.hints,
      },
    };
  }

  return {
    variant: 'success',
    key: 'template.releaseNoFindings',
    params: {},
  };
});

function requestSetInactive() {
  inactiveConfirmIsVisible.value = true;
}

// Releasing warns about the quality check, but never blocks on it.
async function requestRelease() {
  if (!props.templateId) {
    return;
  }

  qualitySummary.value = null;
  isQualityLoading.value = true;
  releaseConfirmIsVisible.value = true;
  qualitySummary.value = await loadQualitySummary(props.templateId);
  isQualityLoading.value = false;
}

async function confirmSetInactive() {
  inactiveConfirmIsVisible.value = false;
  await setStatus('inactive');
}

async function confirmRelease() {
  releaseConfirmIsVisible.value = false;
  await setStatus('released');
}

/**
 * The findings are only readable on the map, so when the check found something
 * the dialog offers a way straight there instead of forcing a blind decision --
 * unless the dialog was opened from inside the template already.
 */
const canOpenTemplate = computed(
  () => route.params.templateId !== props.templateId,
);

function openTemplate() {
  releaseConfirmIsVisible.value = false;
  emit('close');
  router.push({
    name: 'ProjectMapTemplateMap',
    params: { templateId: props.templateId },
  });
}

/**
 * Draft applies straight away; the other two confirm first, because releasing
 * shows the quality check and retiring cuts off `update_from_template`.
 * In Company → Templates releasing applies straight away too.
 */
function selectStatus(next: TemplateStatus) {
  if (next === 'released') {
    return props.manageOwner ? setStatus('released') : requestRelease();
  }
  if (next === 'inactive') {
    return requestSetInactive();
  }

  return setStatus('draft');
}

onMounted(async () => {
  if (!props.templateId) {
    ownerScopes.value = profileStore.profile.scopes.template_creator ?? [];
  } else if (isMeOwner.value) {
    ownerScopes.value = profileStore.profile.scopes.template_reader ?? [];
  }

  if (canManageTemplate.value) {
    void loadOwnerCandidates();
    const previousOwnerId = templateLink.value?.owner?.id;
    if (isMeOwner.value) {
      previousOwnerReaderScopes.value =
        profileStore.profile.scopes.template_reader ?? [];
    } else if (previousOwnerId) {
      void userStore
        .loadUserProfile(previousOwnerId)
        .then((user) => {
          previousOwnerReaderScopes.value = user.scopes?.template_reader ?? [];
          readerScopesByUser.set(
            previousOwnerId,
            previousOwnerReaderScopes.value,
          );
          if (ownerId.value === previousOwnerId) {
            ownerScopes.value = previousOwnerReaderScopes.value;
          }
        })
        .catch(message.error);
    }
  }

  await userStore.loadScopes().then(() => {
    areScopesLoaded.value = true;
  });
});

// Off the map the template list is reloaded: it holds only what the user sees without
// MANAGE_TEMPLATE_OWNER (WI #5789), so a handover can drop the template from it.
async function leaveIfNoLongerVisible(
  templateId: string,
  keptAsEditor: boolean,
) {
  if (route.params.templateId !== templateId) {
    await templateStore.loadList();

    return;
  }
  const canOpen = useTemplateRulesStore().canUserOpenTemplate(
    templateId,
    keptAsEditor ? [profileStore.profile.id] : [],
  );
  if (!canOpen) {
    await router.push({ name: 'Templates' });
  }
}

async function saveTemplate() {
  isLoading.value = true;
  // On create, always send custom_properties so required-validation fires.
  // On edit, only send when the user actually touched the section — this
  // mirrors the backend's conditional gate in updateTemplateDetails and
  // keeps title-only edits possible when admin later adds a required
  // property without default.
  const includeCustomProperties =
    !props.templateId || hasCustomPropertyChanges.value;
  const customPropertiesPayload = includeCustomProperties
    ? {
        custom_properties: customProperties.value as Record<
          string,
          string | number | boolean | string[] | null
        >,
      }
    : {};
  // On edit only what changed: the form may show an older copy, and sending its
  // title and scopes back would undo other users' changes.
  const isTitleChanged = formData.value.title !== initialTitle;
  const areScopesChanged =
    scopesKey.value !== [...initialScopes.value].sort().join(',');
  const action = props.templateId
    ? templateStore.updateDetails(props.templateId, {
        ...(isTitleChanged ? { title: formData.value.title } : {}),
        ...(areScopesChanged ? { scopes: formData.value.scopes } : {}),
        ...(isOwnerChanged.value
          ? {
              owner: ownerId.value,
              keep_previous_owner_as_editor:
                keepPreviousOwnerAsEditor.value &&
                canKeepPreviousOwnerAsEditor.value,
            }
          : {}),
        ...customPropertiesPayload,
      })
    : templateStore.create({ ...formData.value, ...customPropertiesPayload });
  const isHandover = isOwnerChanged.value && !props.manageOwner;
  const keptAsEditor =
    keepPreviousOwnerAsEditor.value && canKeepPreviousOwnerAsEditor.value;

  return await action
    .then(async () => {
      message.success();
      emit('close');
      if (isHandover && props.templateId) {
        await leaveIfNoLongerVisible(props.templateId, keptAsEditor);
      }
    })
    .catch(message.error)
    .finally(() => {
      isLoading.value = false;
    });
}
</script>

<template>
  <UiOverlay v-model="inactiveConfirmIsVisible">
    <UiModal
      :title="$t('template.setInactiveConfirmTitle')"
      size="small"
      :subtitle="$t('template.setInactiveConfirmSubtitle')"
      @close="inactiveConfirmIsVisible = false"
    >
      <template #footer>
        <UiButtonRow
          expand
          is-danger
          :cancel-button-text="$t('button.cancel')"
          :action-button-text="$t('template.setInactive')"
          @cancel="inactiveConfirmIsVisible = false"
          @action="confirmSetInactive"
        />
      </template>
    </UiModal>
  </UiOverlay>

  <UiOverlay v-model="releaseConfirmIsVisible">
    <UiModal
      :title="$t('template.releaseConfirmTitle')"
      :subtitle="$t('template.releaseConfirmSubtitle')"
      size="small"
      @close="releaseConfirmIsVisible = false"
    >
      <UiAlert
        is-wide
        :variant="qualityAlert.variant"
        :title="$t('template.qualityCheck')"
        :content="$t(qualityAlert.key, qualityAlert.params)"
      />
      <template #footer>
        <div class="confirm-footer">
          <UiButton
            v-if="qualityFindingCount && canOpenTemplate"
            variant="primary-text"
            icon="Maximize2"
            @click="openTemplate"
          >
            {{ $t('template.openToReviewFindings') }}
          </UiButton>
          <div class="confirm-footer-actions">
            <UiButton
              variant="default"
              :disabled="isQualityLoading"
              @click="releaseConfirmIsVisible = false"
            >
              {{ $t('button.cancel') }}
            </UiButton>
            <UiButton
              variant="primary"
              :disabled="isQualityLoading"
              @click="confirmRelease"
            >
              {{ $t('template.release') }}
            </UiButton>
          </div>
        </div>
      </template>
    </UiModal>
  </UiOverlay>

  <UiModal
    ref="modalRef"
    title=""
    :is-overflow="!isOwnerSelectOpen"
    @close="emit('close')"
  >
    <template #header>
      <div class="modal-header">
        <div class="title">
          {{
            props.templateId ? $t('template.edit') : $t('template.newTemplate')
          }}
        </div>
        <div class="subtitle-box">
          <div class="subtitle">{{ subtitle }}</div>
          <!-- Applied immediately rather than on Save, like the project edit
               dialog's active switch. -->
          <TemplateStatusSwitcher
            v-if="canManageTemplate"
            class="header-actions"
            :model-value="status"
            :disabled="isStatusLoading || isLoading"
            @select="selectStatus"
          />
        </div>
        <UiButton
          variant="flat"
          class="close-icon-button"
          @click="emit('close')"
        >
          <UiIcon name="XIcon" class="close-icon" />
        </UiButton>
      </div>
    </template>

    <UiForm
      :rules="rules"
      :form-data="formData"
      :disabled="isLoading"
      @form-submit="saveTemplate"
    >
      <template #default="{ formErrors }">
        <UiInput
          v-model="formData.title"
          :label="$t('template.templateTitle')"
          :errors="formErrors.title"
        />
        <template v-if="canManageTemplate">
          <UiSelect
            id="template-owner"
            v-model="ownerId"
            name="owner"
            :label="$t('template.owner')"
            :options="ownerOptions"
            :error="ownerError"
            cant-be-blank
            @open="isOwnerSelectOpen = true"
            @close="isOwnerSelectOpen = false"
          >
            <template #selection="{ selection }">
              <UserTile
                :first-name="getOwner(selection?.value).firstName"
                :last-name="getOwner(selection?.value).lastName"
                :email="getOwner(selection?.value).email"
              />
            </template>
            <template #option="{ option }">
              <UserTile
                :first-name="getOwner(option?.value).firstName"
                :last-name="getOwner(option?.value).lastName"
                :email="getOwner(option?.value).email"
              />
            </template>
          </UiSelect>
          <template v-if="isOwnerChanged">
            <UiAlert is-wide variant="warning" :content="ownerChangeWarning" />
            <UiCheckboxWithLabel
              v-if="canKeepPreviousOwnerAsEditor"
              :value="keepPreviousOwnerAsEditor"
              @toggle="keepPreviousOwnerAsEditor = !keepPreviousOwnerAsEditor"
            >
              {{
                isMeOwner
                  ? $t('templateEditor.keepMeAsEditor')
                  : $t('template.keepPreviousOwnerAsEditor', {
                      name: previousOwnerName,
                    })
              }}
            </UiCheckboxWithLabel>
          </template>
        </template>
        <UiMultiSelectField
          id="template-scopes"
          v-model="formData.scopes"
          :options="scopeOptions"
          :label="`${$t('project.scope')}`"
          :errors="formErrors.scopes"
        />
        <TemplateCustomProperties
          v-model="customProperties"
          v-model:has-validation-errors="hasCustomPropertyErrors"
          v-model:has-user-changes="hasCustomPropertyChanges"
          :template-id="props.templateId"
          :loading="isLoading"
        />
      </template>
      <template #bottom="{ formSubmit, hasErrors }">
        <UiButtonRow
          :cancel-button-text="$t('button.cancel')"
          :action-button-text="
            props.templateId ? $t('button.save') : $t('button.addNew')
          "
          :loading="isLoading"
          :disabled="hasErrors || hasCustomPropertyErrors || !!ownerError"
          expand
          @cancel="emit('close')"
          @action="formSubmit"
        />
      </template>
    </UiForm>
  </UiModal>
</template>

<style lang="scss" scoped>
// "Open template" sits apart from Cancel/Release: it leaves the dialog rather
// than answering it.
.confirm-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-16);
  width: 100%;
  flex-wrap: wrap;
}

.confirm-footer-actions {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  margin-left: auto;
}

// Mirrors the header of the project edit dialog (EditProject.vue), which owns
// the same immediate-apply active switch.
.modal-header {
  display: flex;
  flex-direction: column;
  padding-bottom: var(--space-24);

  .title {
    display: flex;
    gap: var(--space-8);
    font-size: var(--font-size-20);
    line-height: var(--line-height-24);
    color: var(--color-gray-900);
    font-weight: var(--font-weight-medium);
  }

  .subtitle-box {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-8) var(--space-16);
    // German is long enough that the actions and the subtitle cannot share one
    // line; the actions drop below rather than squeezing it into a column.
    flex-wrap: wrap;

    .subtitle {
      font-size: var(--font-size-14);
      color: var(--color-gray-500);
      font-weight: var(--font-weight-regular);
      flex: 1 1 auto;
      min-width: px(200);
    }

    .header-actions {
      width: 100%;
      margin-top: var(--space-12);
    }
  }

  .close-icon-button {
    position: absolute;
    right: 4px;
    top: 12px;
  }
}
</style>
